import { motion } from 'framer-motion';
import clsx from 'clsx';
import { Button } from '@/components/ui/Button';

export interface ReportSegment {
  type: 'h2' | 'p' | 'li';
  text: string;
}

export function parseReportSegments(content: string): ReportSegment[] {
  const segments: ReportSegment[] = [];
  const normalized = content.replace(/([^\n])(##\s+)/g, '$1\n$2');
  const lines = normalized.split(/\r?\n/);
  let buffer = '';

  const flush = () => {
    if (buffer.trim()) segments.push({ type: 'p', text: buffer.trim() });
    buffer = '';
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('## ')) {
      flush();
      segments.push({ type: 'h2', text: trimmed.slice(3).trim() });
    } else if (/^[-*]\s+/.test(trimmed) || /^\d+[.)、]\s+/.test(trimmed)) {
      flush();
      segments.push({
        type: 'li',
        text: trimmed.replace(/^[-*]\s+/, '').replace(/^\d+[.)、]\s+/, ''),
      });
    } else {
      buffer += (buffer ? '\n' : '') + line;
    }
  }
  flush();
  return segments;
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|__[^_]+__|`[^`]+`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    const token = match[0];
    const key = `${match.index}-${token}`;
    if (token.startsWith('`')) {
      nodes.push(
        <code
          key={key}
          className="rounded bg-bg-2 px-1.5 py-0.5 font-mono text-[0.92em] text-accent"
        >
          {token.slice(1, -1)}
        </code>,
      );
    } else {
      nodes.push(
        <strong key={key} className="font-semibold text-fg">
          {token.slice(2, -2)}
        </strong>,
      );
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export function ReportPaper({
  title,
  subtitle,
  progress,
  action,
  children,
}: {
  title: string;
  subtitle: string;
  progress?: number;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-border-accent bg-bg-1 shadow-gold">
      <div className="pointer-events-none absolute right-8 top-6 font-serif text-8xl text-accent opacity-10">
        观
      </div>
      <div className="border-b border-border px-4 py-5 sm:px-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs tracking-[0.3em] text-accent">FATELENS REPORT</p>
            <h2 className="mt-2 font-serif text-xl text-fg sm:text-2xl">{title}</h2>
            <p className="mt-1 text-xs text-fg-muted">{subtitle}</p>
          </div>
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:gap-4">
            {typeof progress === 'number' && <ReportProgress progress={progress} />}
            {action}
          </div>
        </div>
      </div>
      <div className="px-4 py-5 sm:px-8 sm:py-6">{children}</div>
    </section>
  );
}

export function ReportProgress({ progress }: { progress: number }) {
  return (
    <div className="min-w-0 text-left sm:min-w-36 sm:text-right">
      <p className="mb-1 text-xs text-fg-muted">生成进度 {progress}%</p>
      <div className="h-1.5 overflow-hidden rounded-pill bg-bg-2">
        <motion.div
          className="h-full rounded-pill bg-accent"
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.35 }}
        />
      </div>
    </div>
  );
}

export function InsightCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-bg-2 p-4">
      <p className="mb-2 font-serif text-lg text-accent">{title}</p>
      <div className="text-sm leading-7 text-fg-1">{children}</div>
    </div>
  );
}

export function PremiumReportRenderer({
  segments,
  streaming,
}: {
  segments: ReportSegment[];
  streaming?: boolean;
}) {
  let sectionIndex = 0;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
      {segments.map((segment, index) => {
        if (segment.type === 'h2') {
          sectionIndex += 1;
          return (
            <div key={`h2-${index}`} className="flex items-start gap-3 pt-2 sm:items-center">
              <span className="flex h-8 w-8 items-center justify-center rounded-full border border-border-accent bg-accent-soft font-mono text-xs text-accent">
                {String(sectionIndex).padStart(2, '0')}
              </span>
              <h3 className="font-serif text-lg text-accent sm:text-xl">
                {renderInlineMarkdown(segment.text)}
              </h3>
              <span className="h-px flex-1 bg-border" />
            </div>
          );
        }

        if (segment.type === 'li') {
          return (
            <div
              key={`li-${index}`}
              className="flex gap-3 rounded-lg border border-border bg-bg-2 px-4 py-3 text-sm leading-7 text-fg-1"
            >
              <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
              <span>
                {renderInlineMarkdown(segment.text)}
                {streaming && index === segments.length - 1 && <Cursor />}
              </span>
            </div>
          );
        }

        return (
          <p key={`p-${index}`} className="text-sm leading-8 text-fg-1 whitespace-pre-wrap">
            {renderInlineMarkdown(segment.text)}
            {streaming && index === segments.length - 1 && <Cursor />}
          </p>
        );
      })}
    </motion.div>
  );
}

export function LockedReportNotice({ onUnlock }: { onUnlock: () => void }) {
  return (
    <div className="mt-6 rounded-xl border border-dashed border-border-accent bg-accent-soft p-5 text-sm text-fg-1">
      <p className="font-serif text-lg text-accent">完整报告已生成</p>
      <p className="mt-2 leading-7">
        财富与事业、感情与关系、健康与情绪、流年大势、行动清单等章节已封存，解锁后可查看完整交付内容。
      </p>
      <div className="mt-4">
        <Button size="sm" onClick={onUnlock}>
          解锁完整报告
        </Button>
      </div>
    </div>
  );
}

function Cursor() {
  return <span className="ml-1 animate-pulse text-accent">▍</span>;
}

export function ReportEmptyState({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        'rounded-xl border border-dashed border-border bg-bg-2 p-6 text-sm leading-7 text-fg-2',
        className,
      )}
    >
      {children}
    </div>
  );
}
